import Decimal from "decimal.js";
import { createDecimal, divideValues } from "./math";
import { EPSILON } from "utils/numberConstants";

/**
 * Rounds a number to the nearest 10 if `cents` is true, otherwise rounds to the nearest whole number.
 *
 * @param {number} num - The number to round.
 * @param {boolean} [cents=false] - If true, rounds to the nearest 10.
 * @returns {number} - The rounded number.
 */
export const roundNearest = (num, cents = false) => {
  if (cents) {
    return num ? Decimal(num).div(10).round().mul(10).toNumber() : 0;
  } else {
    return num ? Decimal(num).round().toNumber() : 0;
  }
};

/**
 * Rounds a number to two decimal places.
 *
 * @param {number} num - The number to round.
 * @returns {number} - The rounded number to two decimal places.
 */
export const roundNumber = (num) =>
  createDecimal(num).add(EPSILON).mul(100).round().div(100).toNumber();
/**
 * Calculates the percentage of a value relative to a total and formats it as a string.
 *
 * @param {number} value - The value to calculate the percentage for.
 * @param {number} total - The total value.
 * @returns {string} - The percentage value formatted to two decimal places with a "%" symbol.
 */
export const parsePercent = (value, total) => {
  let number = new Decimal(0);
  if (total && value) {
    number = divideValues([value, total]);
  }
  return Decimal(number).mul(100).toFixed(2) + "%";
};

/**
 * Converts a number into a percentage string.
 *
 * @param {number} num - The number to convert.
 * @returns {string} - The number formatted as a percentage with two decimal places.
 */
export const convertToPercent = (num) => {
  return createDecimal(num).mul(100).toFixed(2) + "%";
};

/**
 * Checks if a given value exceeds a specified maximum.
 *
 * @param {number|string} value - The value to check.
 * @param {number} maximum - The maximum allowable value.
 * @returns {boolean} - True if the value exceeds the maximum, false otherwise.
 */
export const checkExceedMaximum = (value, maximum) => {
  const parsedValue = createDecimal(value);
  const maximumValue = createDecimal(maximum);
  return parsedValue.greaterThan(maximumValue);
};

/**
 * Formats a numeric value representing cents into a string with two decimal places representing cents.
 *
 * @param {number} value - The value in cents to be formatted. Defaults to 0 if not provided.
 * @returns {string} The formatted value as a string in dollars with two decimal places.
 */
export const formatValueToCents = (value) => {
  if (value === undefined || value === null) {
    return "0.00";
  }

  try {
    // If invalid, Decimal(value) will throw.
    const decimalValue = Decimal(value);

    return decimalValue.div(100).toFixed(2);
  } catch {
    return "NaN";
  }
};

/**
 * formatToTwoDecimals
 * --------------------
 * Converts a given number to a string with exactly two decimal places.
 * Useful for formatting currency or standardized numerical output.
 *
 * @param {number} num - The number to format.
 * @returns {string} - A string representation of the number with two decimal places.
 *
 * Example:
 * formatToTwoDecimals(100);     // "100.00"
 * formatToTwoDecimals(150.5);   // "150.50"
 * formatToTwoDecimals(144.44);  // "144.44"
 */
export const formatToTwoDecimals = (num) => {
  if (num === undefined || num === null) {
    return "0.00";
  }

  try {
    const decimalValue = Decimal(num);

    return decimalValue.toFixed(2);
  } catch {
    return "NaN";
  }
};

/**
 * parseIntDecimal
 * --------------------
 * Converts a given value to an integer.
 *
 * @param {string|number} num - The value to convert to an integer.
 * @returns {number} - A numeric representation of the value, truncated to an integer.
 *
 * Example:
 * parseInteger(100);     // 100
 * parseInteger(150.5);   // 150
 * parseInteger(144.44);  // 144
 */
export const parseIntDecimal = (num) => {
  if (typeof num !== "string") num = String(num);

  const trimmedNumber = num.trim().replace(/[a-zA-Z]/g, "");
  if (trimmedNumber.length === 0) return 0;

  return new Decimal(trimmedNumber).trunc().toNumber();
};

/**
 * floorValue
 * --------------------
 * Converts a given value and rounds down to nearest integer
 *
 * @param {string|number} value - The value to convert to an integer.
 * @returns {number} - A numeric representation of the value, truncated to an integer.
 *
 * Example:
 * floorValue(100);     // 100
 * floorValue(150.5);   // 150
 * floorValue(144.66);  // 144
 */
export const floorValue = (value) => {
  return createDecimal(value).floor().toNumber();
};

/**
 * parseFloatValue
 * --------------------
 * Converts a string representation of a value and converts to a number
 *
 * @param {string} value - The value to convert to an integer.
 * @returns {number} - A numeric representation of the value
 *
 * Example:
 * parseFloatValue("100");     // 100
 * parseFloatValue("150.5");   // 150.5
 * parseFloatValue("144.66");  // 144.66
 */
export const parseFloatValue = (value) => {
  return createDecimal(value).toNumber();
};

/**
 * absValue
 * --------------------
 * Converts a value to its absolute value
 *
 * @param {string|number} value - The value to get the absolute value of.
 * @returns {number} - A numeric representation of the value
 *
 * Example:
 * absValue(100);     // 100
 * absValue(-150.5);   // 150.5
 */
export const absValue = (value) => {
  return createDecimal(value).abs().toNumber();
};
